import {Injectable, Injector} from '@angular/core';
import * as _ from 'lodash-es';
import {Dimension, Point} from '../layout/abstract-units';
import {getNewCellId} from '../layout/cell';
import {LayoutFactoryBase} from '../layout/layout-factory-base';
import {Widget} from '../layout/widget/widget';
import {WidgetDefinition} from '../layout/widget/widget-definition';
import {WidgetFactory} from '../layout/widget/widget.factory';
import {FloatCell} from './float-cell';
import {FloatLayout} from './float-layout';
import {FloatCellDefinition, FloatLayoutDefinition} from './float-layout.definition';

const DEFAULT_EMPTY_FLOAT_LAYOUT_SIZE: Point = [100, 100];

@Injectable()
export class FloatLayoutFactory extends LayoutFactoryBase {

    constructor(
        private injector: Injector,
        private widgetFactory: WidgetFactory
    ) {
        super();
    }

    public createEmptyFloatLayout(tabId: string): FloatLayout {
        return this.createFloatLayoutByDefinition({
            size: DEFAULT_EMPTY_FLOAT_LAYOUT_SIZE.slice() as Point,
            tabId,
            cells: [],
            widgets: []
        } as FloatLayoutDefinition);
    }

    public createFloatLayoutByDefinition(floatLayoutDef: FloatLayoutDefinition): FloatLayout {
        const size: Point = floatLayoutDef.size;

        // Cells
        const cells: FloatCell[] = floatLayoutDef.cells
            .map(cellDef => this.createFloatCellByDefinition(cellDef, size));
        const cellsDictById = _.keyBy(cells, cell => cell.id);

        // Widgets
        const tabId = floatLayoutDef.tabId;
        floatLayoutDef.widgets.map(widget => widget.tabId = tabId);
        const widgets: Widget[] = this.createWidgetsByDefinition(floatLayoutDef.widgets, cellsDictById);

        this.orderCellsWidgetsByDef(cells, floatLayoutDef);

        // Set active widgets on cells
        this.setCellsActiveWidgetsByDef(cells, floatLayoutDef.cells);

        // Layout
        return new FloatLayout(size, tabId, cells, widgets, this.injector);
    }

    public getFloatLayoutDefinition(floatLayout: FloatLayout): FloatLayoutDefinition {
        return {
            size: floatLayout.size.slice() as Point,
            tabId: floatLayout.tabId,
            cells: floatLayout.cells.map(cell => this.getFloatCellDefinition(cell)),
            widgets: floatLayout.widgets.map(widget => this.widgetFactory.getWidgetDefinition(widget))
        };
    }

    public createFloatCellByDefinition(floatCellDef: FloatCellDefinition, layoutSizeAdjustTo: Point): FloatCell {
        const cell: FloatCell = {
            id:           floatCellDef.id || getNewCellId(),
            widgets:      [],
            activeWidget: null,
            pos:          floatCellDef.pos.slice() as Point,
            size:         floatCellDef.size.slice() as Point,
            display:      null,
            edges:        null
        };

        this.adjustFloatCellPosition(cell, layoutSizeAdjustTo);

        return cell;
    }

    private createWidgetsByDefinition(
        widgetsDef: WidgetDefinition[],
        cellsDictById: {[cellId: string]: FloatCell}
    ): Widget[] {
        return widgetsDef.map(
            widgetDef => {
                const cell: FloatCell = cellsDictById[widgetDef.cellId];

                return this.widgetFactory.createWidgetByDefinition(widgetDef, cell);
            }
        );
    }

    private getFloatCellDefinition(cell: FloatCell): FloatCellDefinition {
        return {
            id: cell.id,
            pos: cell.pos.slice() as Point,
            size: cell.size.slice() as Point,
            activeWidgetIndex: cell.widgets.indexOf(cell.activeWidget),
            widgets: cell.widgets.map(widget => widget.id)
        };
    }

    /**
     * Note: cell.size here contains nulls.
     */
    private adjustFloatCellPosition(cell: FloatCell, layoutSize: Point): void {
        const [x, y] = cell.pos;
        const [lw, lh] = layoutSize;

        const MIN_TOP_VISIBLE_PART = 50;
        const MIN_LEFT_VISIBLE_PART = 50;

        const minX = 0;
        const minY = 0;
        const maxX = lw - MIN_LEFT_VISIBLE_PART;
        const maxY = lh - MIN_TOP_VISIBLE_PART;

        if (x !== null && x !== undefined) {
            cell.pos[Dimension.X] = _.clamp(x, minX, maxX);
        }

        if (y !== null && y !== undefined) {
            cell.pos[Dimension.Y] = _.clamp(y, minY, maxY);
        }
    }

}
